<template>
	<div class="planner-datebar">
		<div class="mb-4">
			<div class="display-month" aria-label="Month displayed">{{ displayMonth }}</div>
		</div>

		<div class="d-flex">
			<button
				class="btn btn-simple d-none d-lg-inline"
				:disabled="prevDisabled"
				data-testid="prev-week"
				type="button"
				@click="prevWeek()"
			>
				<i class="bi-chevron-left"></i>
				<span class="visually-hidden">Previous Week</span>
			</button>

			<div
				ref="datepicker"
				class="datepicker flex-fill"
				data-testid="datepicker"
				@scroll="handleScroll"
			>
				<div
					v-for="(week, weekIndex) in weeks"
					:key="weekIndex"
					:data-week-index="weekIndex"
					class="week"
					data-testid="week"
				>
					<button
						v-for="(day, dayIndex) in week"
						:key="dayIndex"
						class="btn btn-simple btn-day"
						:aria-current="isToday(day.dayObject) ? 'date' : undefined"
						:class="{
							selected: isDaySelected(day.dayObject),
							today: isToday(day.dayObject)
						}"
						type="button"
						data-testid="day"
						@click="onDayClick(day)"
					>
						<div aria-hidden="true">
							{{ formatDate(day.dayObject, 'dd').charAt(0) }}<br />
							<span class="day-number" :class="{ 'fw-bold': day.timeSlots.length > 0 }">
								{{ formatDate(day.dayObject, 'D') }}
							</span>
						</div>
						<div class="visually-hidden">
							<span>{{ formatDate(day.dayObject, 'dddd MMMM D') }}</span>
						</div>
					</button>
				</div>
			</div>

			<button
				class="btn btn-simple d-none d-lg-inline"
				:disabled="nextDisabled"
				data-testid="next-week"
				@click="nextWeek()"
			>
				<i class="bi-chevron-right"></i>
				<span class="visually-hidden">Next Week</span>
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, type Ref, ref, type PropType, onMounted } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { useDates } from '@/composables/use-dates';
import { chunk } from '@/util/array';
import dayjs, { Dayjs } from 'dayjs';
import type { AemBookingDay } from 'types/aemui/schedule-a-tour/scheduleATour';

const props = defineProps({
	modelValue: {
		type: Object as PropType<AemBookingDay | null>,
		default: null
	},
	days: {
		type: Array as PropType<AemBookingDay[]>,
		required: true
	}
});

const emit = defineEmits<{
	(e: 'update:modelValue', value: AemBookingDay): void;
}>();

const { formatDate } = useDates();

const datepicker: Ref<HTMLElement | null> = ref(null);
const handleScroll = useDebounceFn((event: Event) => {
	const target = event.target as HTMLElement;
	const prevSelectedWeek = selectedWeek.value;
	selectedWeek.value = Math.round(target.scrollLeft / target.clientWidth);
	if (prevSelectedWeek > selectedWeek.value) {
		prevWeek(false);
	}
	if (prevSelectedWeek < selectedWeek.value) {
		nextWeek(false);
	}
}, 250);

const today: Ref<Dayjs> = ref(dayjs());
const weeks = computed(() => chunk<AemBookingDay>(props.days, 7));
const selectedWeek = ref(0);

onMounted(() => {
	const isNextWeek = props.modelValue?.dayObject.diff(props.days[0].dayObject, 'day') ?? 0;
	if (isNextWeek > 6) {
		nextWeek();
	}
});

const prevDisabled = computed(() => selectedWeek.value === 0);
const nextDisabled = computed(() => selectedWeek.value === 1);
const sunday = computed(() => weeks.value[selectedWeek.value][0].dayObject);
const saturday = computed(() => weeks.value[selectedWeek.value][1].dayObject);

const displayMonth = computed(() => {
	if (sunday.value.month() !== saturday.value.month()) {
		return `${formatDate(sunday.value, 'MMM')} / ${formatDate(saturday.value, 'MMM')}`;
	}

	return formatDate(sunday.value, 'MMM');
});

const prevWeek = (scroll = true) => {
	const newDay = props.modelValue?.dayObject.subtract(7, 'day');
	const prevWeekDay = props.days.find((day) => day.dayObject.isSame(newDay, 'day'));
	if (!prevWeekDay) {
		return;
	}

	if (scroll && datepicker.value) {
		selectedWeek.value--;
		datepicker.value.scrollTo({
			left: datepicker.value.scrollLeft - datepicker.value.clientWidth,
			behavior: 'smooth'
		});
	}

	emit('update:modelValue', prevWeekDay);
};

const nextWeek = (scroll = true) => {
	const newDay = props.modelValue?.dayObject.add(7, 'day');
	const nextWeekDay = props.days.find((day) => day.dayObject.isSame(newDay, 'day'));
	if (!nextWeekDay) {
		return;
	}

	if (scroll && datepicker.value) {
		selectedWeek.value++;
		datepicker.value.scrollTo({
			left: datepicker.value.scrollLeft + datepicker.value.clientWidth,
			behavior: 'smooth'
		});
	}

	emit('update:modelValue', nextWeekDay);
};

const onDayClick = (day: AemBookingDay) => {
	emit('update:modelValue', day);
};

const isDaySelected = (day: Dayjs) => {
	return props.modelValue?.dayObject.isSame(day, 'day') ?? false;
};

const isToday = (day: Dayjs) => {
	return today.value.isSame(day, 'day');
};

defineExpose({
	handleScroll,
	selectedWeek
});
</script>

<style lang="scss" scoped>
.planner-datebar {
	position: relative;
	top: 0;
	margin-top: 4rem 0;
	border-bottom: solid 1px var(--kpl-border-color-translucent);
	z-index: 9;

	.datepicker {
		overflow-x: auto;
		scroll-snap-type: x mandatory;
		white-space: nowrap;

		&::-webkit-scrollbar {
			display: none;
		}
	}

	.week {
		display: inline-flex;
		width: 100%;
		scroll-snap-align: start;
		scroll-snap-stop: always;
	}

	.btn-day {
		display: flex;
		align-items: center;
		justify-content: flex-start;
		flex-direction: column;
		flex: 0 0 auto;
		width: 14.285714285%;
		min-height: 4rem;
		position: relative;
		line-height: 1;
		border-radius: 0;

		&:hover {
			opacity: 100;
		}
	}

	.day-number {
		display: flex;
		align-items: center;
		justify-content: center;
		height: 32px;
		width: 32px;
		margin: 0.5rem 0;
		padding-left: 1px;
		border-radius: 50%;
	}

	.today .day-number {
		border: 1px solid color-mix(in sRGB, currentColor 25%, transparent);
	}

	.selected .day-number {
		background-color: var(--kpl-body-color);
		color: var(--kpl-body-bg);
		opacity: 1;
	}

	.display-month {
		opacity: 0.75;
		text-transform: uppercase;
	}
}
</style>
